import { createContext, useContext } from 'react';
import { AI_BUILDER_MENTION_ID, AI_BUILDER_MENTION_TYPE } from '@shared/task-contexts';
import { TASK_AI_BUILDER_NAME, type TaskContextSearchHit, type TaskMember } from '@shared/types/tasks';
import type { PickedMention } from './mention-text';
import { memberName } from './task-chips';

/**
 * Whether this team has the AI builder (T-393), for the task text under it: the @ list offers
 * "AI builder" and its mentions show as mentions. Off, a pasted one is plain text.
 */
export const AiBuilderMentions = createContext(false);

export function useAiBuilderMentions(override?: boolean): boolean {
  const fromTask = useContext(AiBuilderMentions);
  return override ?? fromTask;
}

export const AI_BUILDER_PICK: PickedMention = { label: TASK_AI_BUILDER_NAME, type: AI_BUILDER_MENTION_TYPE, id: AI_BUILDER_MENTION_ID };

/** The AI builder matches an empty query, or one a word of its name starts with ("ai", "build", "ai b"). */
export function aiBuilderMatches(query: string): boolean {
  const q = query.trim().toLowerCase();
  const name = TASK_AI_BUILDER_NAME.toLowerCase();
  return !q || name.startsWith(q) || name.split(' ').some((word) => word.startsWith(q));
}

export type MentionSuggestion = { kind: 'user'; member: TaskMember } | { kind: 'ai' } | { kind: 'context'; hit: TaskContextSearchHit };

/** What the @ list offers for a query: up to five people, the AI builder when the team has it, then records. */
export function mentionSuggestions(query: string, members: TaskMember[], hits: TaskContextSearchHit[], aiBuilder: boolean): MentionSuggestion[] {
  const q = query.toLowerCase();
  const people = members
    .filter(
      (m) =>
        !q || memberName(m).toLowerCase().includes(q) || m.email.toLowerCase().startsWith(q) || Boolean(m.handle?.toLowerCase().startsWith(q))
    )
    .slice(0, 5)
    .map((member): MentionSuggestion => ({ kind: 'user', member }));
  const ai: MentionSuggestion[] = aiBuilder && aiBuilderMatches(q) ? [{ kind: 'ai' }] : [];
  return [...people, ...ai, ...hits.map((hit): MentionSuggestion => ({ kind: 'context', hit }))];
}
